const _ = require("lodash");
const CanvasMath = require("../../../core/canvasmath/CanvasMath");
const Color = require("color");
const Math2D = require("../../../core/util/Math2D");
const GroupGeometry = require("../../util/GroupGeometry");
const ArrowDraw = require("../../../core/util/ArrowDraw");
const DrawRectUtil = require("../../../core/util/DrawRectangleUtil");
const DrawImageUtil = require("../../../core/util/DrawImageUtil");

// For now, the maze drawing functions are square geometry only.
const HEX_PATH_LINE_WIDTH = 15;
const makePathFromEdges = function(context, geom, edges) {
	for (let i = 0; i < edges.length; i++) {
		const drawLine = geom.getDrawEdge(...edges[i]);
		context.M(drawLine[0], drawLine[1]).L(drawLine[2], drawLine[3]);
	}
};

//This draws the base grid lines and numbers (if applicable)
const drawGrid = function(context, geom, cS, isFillBehavior, forceColor) {
	context.save();
	context.strokeStyle =
		forceColor || (isFillBehavior ? cS.gridLine : cS.lightGrayFill);
	context.beginPath();
	geom.getDrawGridlines().forEach(line => {
		context.moveTo(line[0], line[1]);
		context.lineTo(line[2], line[3]);
	});
	context.stroke();
	context.restore();
};

// This draws the grid without the removed edges
const drawPartialGrid = function(context, geom, cS, removedEdges, forceColor) {
	context.save();
	context.strokeStyle = forceColor || cS.gridLine;
	context.beginPath();
	// TODO generate grid lines
	geom.getPartialGridlines(removedEdges).forEach(line => {
		context.moveTo(line[0], line[1]);
		context.lineTo(line[2], line[3]);
	});
	context.stroke();
	context.restore();
};

//Loops through the group grid and draws lines between non-connected cells,
const drawGroupGrid = function(context, geom, cS, groupGrid, contextSettings) {
	const edges = GroupGeometry.getAllBoundaries(geom, groupGrid);
	//Drawing all those lines
	context.save();
	context.strokeStyle = cS.gridLine;
	context.lineCap = "round";
	context.lineWidth *= 1.6;
	if (!_.isEmpty(contextSettings)) {
		_.each(contextSettings, (value, key) => {
			context[key] = value;
		});
	}
	context.beginPath();
	makePathFromEdges(context, geom, edges);
	context.stroke();
	context.restore();
};

//Draws the outline of one group on the grid.
const drawGroupOutline = function(
	context,
	geom,
	groupGrid,
	groupSquare,
	opts = {}
) {
	context.save();
	if (opts.lineWidth) {
		context.lineWidth = opts.lineWidth;
	}
	if (opts.strokeStyle) {
		context.strokeStyle = opts.strokeStyle;
	}
	context.beginPath();
	context.lineCap = "round";
	const edges = GroupGeometry.getGroupBoundary(geom, groupGrid, groupSquare);
	makePathFromEdges(context, geom, edges);
	context.stroke();
	context.restore();
};

const drawGroupHover = function(
	context,
	geom,
	cS,
	groupGrid,
	groupSquare,
	isIncorrect
) {
	if (!groupSquare) {
		return;
	}
	drawGroupOutline(context, geom, groupGrid, groupSquare, {
		lineWidth: context.lineWidth * 3,
		strokeStyle: isIncorrect ? cS.incorrectFill : cS.defaultBlackFill,
	});
};

const rectSquaresOfType = function(
	context,
	geom,
	numRows,
	numCols,
	shouldFill
) {
	for (let col = 0; col < numCols; col++) {
		for (let row = 0; row < numRows; row++) {
			if (shouldFill(row, col)) {
				const rect = geom.getDrawCellRect(row, col);
				context.rect(rect.x, rect.y, rect.w, rect.h);
			}
		}
	}
};

// Fill in squares using appropriate colors (from getColor function)
const fillSquaresOfType = function(
	context,
	geom,
	numRows,
	numCols,
	shouldFill,
	getColor
) {
	for (let col = 0; col < numCols; col++) {
		for (let row = 0; row < numRows; row++) {
			if (shouldFill(row, col) && getColor(row, col)) {
				const rect = geom.getDrawCellRect(row, col);
				context.fillStyle = getColor(row, col).dark;
				context.fillRect(rect.x, rect.y, rect.w, rect.h);
			}
		}
	}
};

// If onUpperRight is true, X's are drawn in the upper right corner. Otherwise, they're in the center.
const drawPartsCorrectXMarks = function(
	context,
	geom,
	cS,
	selectedSquares,
	partsCorrect,
	onUpperRight,
	color // can be undefined (if using default color, cS.incorrectFill)
) {
	//And the incorrect not selected squares (should have been selected)
	context.save();
	context.fillStyle = _.isUndefined(color) ? cS.incorrectFill : color;
	const numCols = selectedSquares.length;
	const numRows = selectedSquares[0].length;
	for (let j = 0; j < numCols; j++) {
		for (let i = 0; i < numRows; i++) {
			if (!partsCorrect[j][i]) {
				const rect = geom.getDrawCellRect(i, j);
				const xRect = onUpperRight
					? {
							x: rect.x + (2 * rect.w) / 3,
							y: rect.y,
							w: rect.w / 3,
							h: rect.h / 3,
					  }
					: rect;
				CanvasMath.drawInRect(context, {
					batex: "\\xmark",
					rect: Math2D.padRectProportional(xRect, -0.1, -0.1),
				});
			}
		}
	}
	context.restore();
};

const drawGroupReaction = function(
	context,
	geom,
	cS,
	groupGrid,
	groupSquares,
	glowAlpha
) {
	context.save();
	context.globalAlpha = glowAlpha;
	groupSquares.forEach(groupSquare => {
		drawGroupOutline(context, geom, groupGrid, groupSquare, {
			lineWidth: context.lineWidth * 2,
			strokeStyle: cS.incorrectFill,
		});
	});
	context.restore();
};

const drawGroupForceSelectedSquares = function(
	context,
	geom,
	cS,
	forceSelectedSquares,
	valueColors // can be undefined (Abstract Art)
) {
	context.save();
	if (!forceSelectedSquares) return;
	const numCols = forceSelectedSquares.length;
	const numRows = forceSelectedSquares[0].length;
	const shouldFill = (row, col) => forceSelectedSquares[col][row] === 1;

	if (valueColors) {
		// for engines where locked squares may be filled in different colors,
		// get fill color from valueColors
		const getColor = (row, col) =>
			valueColors && valueColors[forceSelectedSquares[col][row]];
		fillSquaresOfType(context, geom, numRows, numCols, shouldFill, getColor);
	} else {
		context.fillStyle = cS.inputContrastFill;
		context.beginPath();
		rectSquaresOfType(context, geom, numRows, numCols, shouldFill);
		context.fill();
	}

	const lockImg =
		global.BASE_URL + "assets/images/problem-art/cell-lock-gray.svg";
	const padding = geom.dim.size / 6;

	for (let col = 0; col < numCols; col++) {
		for (let row = 0; row < numRows; row++) {
			if (forceSelectedSquares[col][row]) {
				drawImageInCell(context, geom, cS, lockImg, row, col, padding, padding);
			}
		}
	}

	context.restore();
};

const drawGroupSelectedSquares = function(context, geom, cS, selectedSquares) {
	context.save();
	const numCols = selectedSquares.length;
	const numRows = selectedSquares[0].length;

	//First filling in all the selected squares of types 1 and 2
	context.fillStyle = cS.inputContrastFill;
	context.beginPath();
	rectSquaresOfType(
		context,
		geom,
		numRows,
		numCols,
		(i, j) => selectedSquares[j][i] === 1
	);
	context.fill();

	context.fillStyle = cS.inputContrastSecondaryFill;
	context.beginPath();
	rectSquaresOfType(
		context,
		geom,
		numRows,
		numCols,
		(i, j) => selectedSquares[j][i] === 2
	);
	context.fill();

	context.restore();
};

const drawNumbers = function(context, geom, cS, gridNumbers, opts = {}) {
	const {
		lessNumberPadding = false,
		sizeFontForNumBatexChars = false,
		infoFunc,
		displayZeros = false,
	} = opts;
	let {fontSize} = opts;

	context.save();
	let count = 1;
	let yPadding = Math.max(5, geom.dim.size / 5);
	let xPadding = 0;
	if (lessNumberPadding) {
		yPadding = Math.max(3, geom.dim.size / 6);
		xPadding = 1;
	}
	let rect;
	let batex;
	let drawOptions;
	geom.getAllCells().forEach(coords => {
		rect = geom.getDrawCellRect(coords[0], coords[1]);
		batex = gridNumbers ? gridNumbers[coords[1]][coords[0]] : count;
		if (displayZeros && batex === 0) {
			batex = "0";
		} else {
			batex = batex || "";
		}
		if (infoFunc) {
			const info = infoFunc(coords);
			context.fillStyle = info.color || context.fillStyle;
			if (info.isBold) {
				batex = "\\bold{" + batex + "}";
			}
		}

		const rectY = rect.y + (opts.hasLabels ? 2 * yPadding : yPadding);

		drawOptions = {
			batex: batex,
			rect: [
				rect.x + xPadding,
				rectY,
				rect.w - 2 * xPadding,
				rect.h - 2 * yPadding,
			],
		};
		//If sizeFontForNumBatexChars is given, use that to compute the fontSize that should be used
		if (sizeFontForNumBatexChars) {
			fontSize = (geom.dim.size * 4) / 3 / (sizeFontForNumBatexChars + 0.2);
		}
		// If a font size is given, then we use that without scaling.
		// Need this so each cell in a griddy grid doesn't have differently
		// scaled font.
		if (fontSize) {
			drawOptions.scaleOff = true;
			drawOptions.fontInfo = {
				size: fontSize,
				family: ["Roboto"],
			};
		}

		CanvasMath.drawInRect(context, drawOptions);
		count++;
	});
	context.restore();
};

const drawNumberInCell = function(
	context,
	geom,
	cS,
	number,
	cellX,
	cellY,
	color,
	lessNumberPadding,
	fontSize,
	opts = {}
) {
	context.save();
	const rect = geom.getDrawCellRect(cellX, cellY);
	const yPadding = lessNumberPadding
		? Math.max(3, geom.dim.size / 6)
		: Math.max(5, geom.dim.size / 5);
	const xPadding = lessNumberPadding ? 1 : 0;

	let x = rect.x + xPadding;
	let y = rect.y + yPadding;
	let w = rect.w - 2 * xPadding;
	let h = rect.h - 2 * yPadding;
	// If drawOnEdge is set, we draw the number exactly on the edge of the cell,
	// assuming that one of top, bottom, left, and right is also specified.
	if (opts.drawOnEdge) {
		if (opts.top || opts.bottom) {
			h = 0;
			y = rect.y;
		} else if (opts.left || opts.right) {
			w = 0;
			x = rect.x;
		}

		if (opts.bottom) {
			y = rect.y + rect.h;
		} else if (opts.right) {
			x = rect.x + rect.w;
		}
	} else {
		if (opts.top || opts.bottom) {
			h = Math.min(opts.numberHeight || h * 0.3, h);
		} else if (opts.left || opts.right) {
			w = Math.min(opts.numberWidth || w * 0.5, w);
		}

		if (opts.bottom) {
			y = rect.y + rect.h - yPadding - h;
		} else if (opts.right) {
			x = rect.x + rect.w - xPadding - w;
		}
	}
	const drawOptions = {
		batex: number,
		rect: [x, y, w, h],
	};
	if (fontSize) {
		drawOptions.scaleOff = true;
		drawOptions.fontInfo = {
			size: fontSize,
			family: ["Roboto"],
		};
	}
	context.fillStyle = color || cS.defaultText;
	if (opts.backgroundCircle) {
		const centerX = x + w / 2;
		const centerY = y + h / 2;
		const radius = fontSize ? fontSize / 1.6 : context.lineWidth * 5;

		context.save();
		context.beginPath();
		context.globalAlpha = 0.8;
		if (opts.circleColor) {
			context.fillStyle = opts.circleColor;
		}
		context.arc(centerX, centerY, radius, 0, 360);
		context.fill();
		context.restore();
	}
	CanvasMath.drawInRect(context, drawOptions);
	context.restore();
};

const drawLabelInCell = function(
	context,
	geom,
	cS,
	labelText,
	cellX,
	cellY,
	color,
	lessNumberPadding,
	fontSize
) {
	context.save();
	const rect = geom.getDrawCellRect(cellX, cellY);
	const yPadding = lessNumberPadding
		? geom.dim.size * 0.1
		: geom.dim.size * 0.2;
	const xPadding = lessNumberPadding ? 1 : 0;
	const fracRect = 1 / 2;
	const drawOptions = {
		batex: labelText,
		rect: [
			rect.x + xPadding,
			rect.y + yPadding,
			rect.w - 2 * xPadding,
			rect.h * fracRect - 2 * yPadding,
		],
	};
	if (fontSize) {
		drawOptions.scaleOff = true;
		drawOptions.fontInfo = {
			size: fontSize,
			family: ["Roboto"],
		};
	}
	context.fillStyle = color || cS.defaultText;
	CanvasMath.drawInRect(context, drawOptions);
	context.restore();
};

const drawImageInCell = function(
	context,
	geom,
	cS,
	imageSrc,
	cellX,
	cellY,
	hPadding,
	vPadding,
	rotation = 0,
	isReflected,
	scale = 1
) {
	const rect = geom.getDrawCellRect(cellX, cellY);
	DrawImageUtil.drawImageInRect(
		context,
		imageSrc,
		rect,
		hPadding,
		vPadding,
		rotation,
		isReflected,
		scale
	);
};

// Draws thicker borders around the grid.
const drawGridBorder = function(context, geom, cS) {
	context.save();
	//for hex row grid geometry
	if (geom.style === "hexRows") {
		//only stroke edges that aren't touching another edge
		const edges = geom.getAllNonOverlapEdges();
		drawHexEdges(context, geom, cS, edges, {color: cS.black});
	}
	//for square grid geometry
	else {
		context.lineWidth = context.lineWidth * 2;
		context.lineCap = "round";
		const rect = geom.getDrawFullRect();
		context.strokeRect(rect.x, rect.y, rect.w, rect.h);
	}
	context.restore();
};

const drawSelectedSquares = function(
	context,
	geom,
	cS,
	selectedSquares,
	forceColor,
	allColors,
	alphaMultiplier
) {
	let squareCoords;
	let squareRect;
	context.save();
	if (_.isNumber(alphaMultiplier)) {
		context.globalAlpha *= alphaMultiplier;
	}
	for (const square in selectedSquares) {
		squareCoords = geom.convertCellKey(square);
		context.save();
		if (forceColor) {
			context.fillStyle = forceColor;
			squareRect = geom.getDrawCellRect(squareCoords[0], squareCoords[1]);
			context.fillRect(squareRect.x, squareRect.y, squareRect.w, squareRect.h);
		} else if (!_.isEmpty(selectedSquares[square])) {
			const colorArray = selectedSquares[square];
			let color = Color(colorArray[0]);
			if (colorArray.length > 1) {
				for (let i = 1; i < colorArray.length; i++) {
					color = color.mix(Color(colorArray[i]), 1 / (i + 1));
				}
				color = color.opaquer(1.25);
			}
			context.fillStyle = color.string();
			squareRect = geom.getDrawCellRect(squareCoords[0], squareCoords[1]);
			context.fillRect(squareRect.x, squareRect.y, squareRect.w, squareRect.h);
			if (allColors) {
				colorArray.forEach(c => {
					drawPattern(
						context,
						squareRect,
						cS,
						allColors.indexOf(c),
						false,
						false
					);
				});
			}
		}
		context.restore();
	}
	context.restore();
};

//Allows you to outline a square and choose the color
const drawSquareOutlineColor = (
	context,
	geom,
	selectedSquares,
	color,
	thicknessMultiplier
) => {
	context.save();
	context.lineJoin = "round";
	context.lineWidth =
		context.lineWidth *
		(Number.isInteger(thicknessMultiplier) ? thicknessMultiplier : 2);
	context.strokeStyle = color;
	selectedSquares.forEach(square => {
		const mousedRect = geom.getDrawCellRect(square[0], square[1]);
		context.strokeRect(mousedRect.x, mousedRect.y, mousedRect.w, mousedRect.h);
	});
	context.restore();
};

const drawSquareReaction = function(
	context,
	geom,
	cS,
	selectedSquares,
	glowAlpha
) {
	context.save();
	context.globalAlpha = glowAlpha;
	context.lineJoin = "round";
	context.lineWidth = context.lineWidth * 2;
	context.strokeStyle = cS.incorrectFill;
	selectedSquares.forEach(square => {
		const mousedRect = geom.getDrawCellRect(square[0], square[1]);
		context.strokeRect(mousedRect.x, mousedRect.y, mousedRect.w, mousedRect.h);
	});
	context.restore();
};

const DIAGONAL_DIRECTIONS = {
	LU: 0, // left and up
	LD: 1, // left and down
	RU: 2, // right and up
	RD: 3, // right and down
};

// currently only works in square grids
const drawSelectedDiagonals = function(
	context,
	geom,
	cS,
	selectedDiagonals,
	forceColor,
	opts = {}
) {
	const {LU, LD, RU, RD} = DIAGONAL_DIRECTIONS;

	const {widthMultiplier, alphaMultiplier} = opts;

	context.save();
	context.lineWidth = context.lineWidth * 2 * (widthMultiplier || 1);
	context.globaAlpha = alphaMultiplier || 1;
	context.lineCap = "round";
	for (const diagonal in selectedDiagonals) {
		const color = forceColor || selectedDiagonals[diagonal];
		context.strokeStyle = color;

		// parse diagonal in [col, row, direction] format
		const parsedDiagonal = diagonal.split(",").map(s => parseInt(s));
		let [col, row] = parsedDiagonal;
		const direction = parsedDiagonal[2];

		// calculate start and end directions
		const [startX, startY] = geom.getDrawEdge(col, row, false);
		switch (direction) {
			case LU:
				col--;
				row--;
				break;
			case LD:
				col--;
				row++;
				break;
			case RU:
				col++;
				row--;
				break;
			case RD:
				col++;
				row++;
				break;
			default:
				break;
		}
		const [endX, endY] = geom.getDrawEdge(col, row, false);

		// actual drawing
		context.beginPath();
		context.moveTo(startX, startY);
		context.lineTo(endX, endY);
		context.stroke();
	}
	context.restore();
};

const drawSelectedEdges = function(
	context,
	geom,
	cS,
	selectedEdges,
	xOverEdges,
	forceColor,
	opts = {}
) {
	const {widthMultiplier, alphaMultiplier} = opts;

	let color;
	let edgeCoords;
	let edgeDrawCoords;
	context.save();
	context.lineWidth = context.lineWidth * 2 * (widthMultiplier || 1);
	context.globalAlpha = alphaMultiplier || 1;
	context.lineCap = "round";
	for (const edge in selectedEdges) {
		color = forceColor || selectedEdges[edge];
		edgeCoords = geom.convertEdgeKey(edge);
		if (_.isArray(xOverEdges) && !forceColor) {
			_.each(xOverEdges, xOverEdge => {
				if (xOverEdge === edge) {
					color = cS.darkGrayFill;
				}
			});
		}
		context.strokeStyle = color;
		edgeDrawCoords = geom.getDrawEdge(
			edgeCoords[0],
			edgeCoords[1],
			edgeCoords[2]
		);
		context.beginPath();
		context.moveTo(edgeDrawCoords[0], edgeDrawCoords[1]);
		context.lineTo(edgeDrawCoords[2], edgeDrawCoords[3]);
		context.stroke();
	}
	context.restore();
};

const drawVertexDot = function(context, geom, cS, col, row, opts = {}) {
	const [x, y] = geom.getDrawVertex(col, row);
	const radius = opts.radiusOpt || context.lineWidth * 5;
	const alpha = opts.alphaMultiplier || 1;

	context.save();
	context.beginPath();
	context.globalAlpha = alpha;
	if (opts.colorOpt) {
		context.fillStyle = opts.colorOpt;
	}
	context.arc(x, y, radius, 0, 360);
	context.fill();
	context.restore();
};

/**
 * This function draws edges that you can pass in starting and ending points for
 *
 * edges is an array of "edge"s.
 * Each edge looks like [startPoint, endPoint]
 * And each startPoint and endPoint look like [x, y] where x and y are coordinates
 * Example:
 * ```
 * edge1 = [[start1X,start1Y],[end1X,end1Y]]
 * edge2 = [[start2X,start2Y],[end2X,end2Y]]
 * edges = [edge1, edge2]
 * ```
 * @param {Object} context - The context for the problem. Basically a sort of canvas that can be drawn on.
 * @param {Object} geom - Information about the geometry of the problem. Contains info like the shape, rows, cols, etc.
 * @param {Object} cS - An object with the entire color scheme for the aops website
 * @param {array} edges - An array of depth 3 containing the information about each edge that should be drawn on the context.
 * @param {string} color - A string representation of the color for the lines of the edges
 * @param {number} alpha - A number between 0.0 and 1.0 that represents how opaque the context should be. 1 is fully opaque, 0 is fully transparent.
 */
const drawEdges = function(context, geom, cS, edges, {color, alpha = 1}) {
	context.save();
	context.lineWidth = context.lineWidth * 2;
	context.lineCap = "round";
	context.strokeStyle = color;
	context.globalAlpha = alpha;
	context.beginPath();
	_.each(edges, edge => {
		const drawPoints = _.map(edge, x => geom.getDrawVertex(x[0], x[1]));
		context.moveTo(...drawPoints[0]);
		context.lineTo(...drawPoints[1]);
	});
	context.stroke();
	context.restore();
};

const drawHexEdges = function(context, geom, cS, edges, {color, alpha = 1}) {
	context.save();
	context.lineWidth = context.lineWidth * 1.5;
	context.lineCap = "round";
	context.strokeStyle = color;
	context.globalAlpha = alpha;
	context.beginPath();
	_.each(edges, edge => {
		const edgeVertices = geom.getEdgeVertices(edge[0], edge[1], edge[2]);
		const drawPointStart = geom.getDrawVertex(
			edgeVertices[0][0],
			edgeVertices[0][1]
		);
		const drawPointEnd = geom.getDrawVertex(
			edgeVertices[1][0],
			edgeVertices[1][1]
		);
		context.moveTo(...drawPointStart);
		context.lineTo(...drawPointEnd);
	});

	context.closePath();
	context.stroke();
	context.restore();
};

// Extends edge as far as it can go in both directions.
const drawExtendedEdge = function(context, geom, edge, color) {
	const minX = edge[2] ? 0 : edge[0];
	const maxX = edge[2] ? geom.cols : edge[0];
	const minY = edge[2] ? edge[1] : 0;
	const maxY = edge[2] ? edge[1] : geom.rows;
	const v1 = geom.getDrawVertex(minX, minY);
	const v2 = geom.getDrawVertex(maxX, maxY);
	context.save();
	context.lineWidth = context.lineWidth * 2;
	context.lineCap = "round";
	context.strokeStyle = color;
	context.beginPath();
	context.moveTo(v1[0], v1[1]);
	context.lineTo(v2[0], v2[1]);
	context.stroke();
	context.restore();
};

// The paths parameter is an array of paths, which are an array of string
// keys of cells in the grid.
// The startingCells parameter is an array of length 2 arrays of ints specifying the coordinates of cells where paths should start.
// The exitData parameter is either false (if there is no path exit) or an object with required keys:
// - pathEnd: array coordinates of where the maze ends.
// - pathEndEdge: array coordinates of the edge where the exit should be drawn.
// The pathToDelete parameter is an array of two coordinate arrays, indicating which
// path has an X drawn over it.  Used for coloring that path differently.
// The largeCircles parameter indicates if the circles are larger than average.
// The replaceCircleWithOutline parameter indicates if, for a certain grid cell, the grid cell is outlined rather than having a circle drawn within it.
// extendPath parameter determines if the exit path is extended and drawn outside of the grid.
// drawExtendedArrow parameter indicates, for extendPath = true case, whether the path line is capped with an arrow head.
const drawAllSquarePaths = function(
	context,
	geom,
	cS,
	paths,
	startingCells,
	exitData,
	pathToDelete,
	pathColor,
	largeCircles,
	props = {}
) {
	const {
		replaceCirclesWithOutline,
		radiusRatio,
		exitLineCap,
		extendPath,
		drawExtendedArrow,
		onlyDrawFullPathExitArrow,
		numberOfRemovedSquares,
		alphaMultiplier,
		extendColorBeyondXOverPath,
	} = props;

	let path;
	const contextSettings = {
		lineWidth: context.lineWidth * 4,
		globalAlpha: (alphaMultiplier || 1) * 0.6,
		lineCap: "round",
	};
	context.save();
	for (let i = 0; i < paths.length; i++) {
		path = paths[i];
		const startingKeys = _.map(startingCells, cell => cell.join(","));
		let compDirection = 0;
		if (extendColorBeyondXOverPath) {
			if (_.includes(startingKeys, path[path.length - 1])) {
				compDirection = -1;
			} else {
				compDirection = 1;
			}
		}
		context.beginPath();
		let xIndex = -1;
		for (let j = 0; j < path.length - 1; j++) {
			const firstCoords = geom.convertCellKey(path[j]);
			const secondCoords = geom.convertCellKey(path[j + 1]);
			const jthSegmentIsPathToDelete =
				!_.isNull(pathToDelete) &&
				_.isEqual(pathToDelete[0], firstCoords) &&
				_.isEqual(pathToDelete[1], secondCoords);
			const jthSegmentBackwardsIsPathToDelete =
				!_.isNull(pathToDelete) &&
				_.isEqual(pathToDelete[1], firstCoords) &&
				_.isEqual(pathToDelete[0], secondCoords);
			const hasPathToDelete =
				jthSegmentIsPathToDelete || jthSegmentBackwardsIsPathToDelete;
			contextSettings.strokeStyle = hasPathToDelete
				? cS.incorrectFill
				: pathColor;
			if (hasPathToDelete) xIndex = j;

			if (!_.isNull(pathToDelete)) {
				if (
					(compDirection === 1 && xIndex !== -1 && j > xIndex) ||
					(compDirection === -1 && xIndex === -1)
				) {
					contextSettings.strokeStyle = cS.incorrectFill;
				}
			}
			// If the path is the path to be deleted, it needs to be a different
			// color from the rest of the maze path. That means we need to
			// start a new context path.
			if (hasPathToDelete) {
				context.stroke();
			}
			drawSquarePath(
				context,
				geom,
				this.cS,
				firstCoords,
				secondCoords,
				startingCells,
				exitData,
				hasPathToDelete,
				!hasPathToDelete,
				largeCircles,
				contextSettings,
				{
					replaceCirclesWithOutline,
					radiusRatio,
					exitLineCap,
					extendPath,
					drawExtendedArrow:
						drawExtendedArrow && !onlyDrawFullPathExitArrow
							? true
							: path.length ===
							  geom.getAllCells().length - (numberOfRemovedSquares || 0),
				}
			);
			if (hasPathToDelete) {
				context.beginPath();
			}
		}
		context.stroke();
	}
	context.restore();
};

const hexSizeMultiplier = 21;
const drawAllHexPaths = function(
	context,
	geom,
	cS,
	paths,
	startingHexes,
	exitData,
	xOverPath,
	pathColor,
	largeCircles,
	props = {}
) {
	const {
		replaceCirclesWithOutline,
		radiusRatio,
		exitLineCap,
		extendPath,
		drawExtendedArrow,
		onlyDrawFullPathExitArrow,
		numberOfRemovedHexes,
		alphaMultiplier,
		extendColorBeyondXOverPath,
	} = props;

	let path;
	const contextSettings = {
		lineWidth: context.lineWidth * hexSizeMultiplier,
		globalAlpha: (alphaMultiplier || 1) * 0.6,
		lineCap: "round",
	};
	context.save();
	for (let i = 0; i < paths.length; i++) {
		path = paths[i];
		const startingKeys = _.map(startingHexes, hex => hex.join(","));
		let compDirection = 0;
		if (extendColorBeyondXOverPath) {
			if (_.includes(startingKeys, path[0])) {
				compDirection = 1;
			} else if (_.includes(startingKeys, path[path.length - 1])) {
				compDirection = -1;
			}
		}
		context.beginPath();
		let xIndex = -1;
		for (let j = 0; j < path.length - 1; j++) {
			const firstCoords = geom.convertCellKey(path[j]);
			const secondCoords = geom.convertCellKey(path[j + 1]);
			const hasXOverPath =
				!_.isNull(xOverPath) &&
				((_.isEqual(xOverPath[0], firstCoords) &&
					_.isEqual(xOverPath[1], secondCoords)) ||
					(_.isEqual(xOverPath[1], firstCoords) &&
						_.isEqual(xOverPath[0], secondCoords)));
			contextSettings.strokeStyle = hasXOverPath ? cS.incorrectFill : pathColor;
			if (hasXOverPath) xIndex = j;

			if (!_.isNull(xOverPath) && compDirection !== 0) {
				if (
					(compDirection === 1 && xIndex !== -1 && j > xIndex) ||
					(compDirection === -1 && xIndex === -1)
				) {
					contextSettings.strokeStyle = cS.incorrectFill;
				}
			}
			// If the hex path has an X over it, it needs to be a different
			// color from the rest of the paths path.  That means we need to
			// start a new context path.
			if (hasXOverPath) {
				context.stroke();
			}
			const drawExtArrow =
				drawExtendedArrow && !onlyDrawFullPathExitArrow
					? true
					: path.length ===
					  geom.getAllCells().length - (numberOfRemovedHexes || 0);
			drawHexPath(
				context,
				geom,
				this.cS,
				firstCoords,
				secondCoords,
				startingHexes,
				exitData,
				hasXOverPath,
				!hasXOverPath,
				largeCircles,
				contextSettings,
				{
					replaceCirclesWithOutline,
					radiusRatio,
					exitLineCap,
					extendPath,
					drawExtendedArrow: drawExtArrow,
				}
			);
			if (hasXOverPath) {
				context.beginPath();
			}
		}
		context.stroke();
	}
	context.restore();
};

// Draws a line from center of one square to center of another.
// firstCoords and secondCoords are length 2 arrays of ints specifying square coordinates for path endpoints.
// The startingSquares parameter is an array of length 2 arrays of ints specifying the coordinates of squares where paths should start.
// The exitData parameter should be the same format as in drawAllSquarePaths.
// The contextSettings parameter is an object that may contain these properties which set the context:
// lineWidth, globalAlpha, lineCap, strokeStyle.
// The doNotStroke parameter determines if we should avoid stroking the path.
// We'd avoid stroking if we're drawing a cell path
// (we need all of the paths to be drawn in one stroke as opposed to lots of individual strokes).
// The largeCircles parameter indicates if the circles are larger than average.
// The replaceCircleWithOutline parameter indicates if, for a certain grid cell, the grid cell is outlined rather than having a circle drawn within it.
// extendPath parameter determines if the exit path is extended and drawn outside of the grid.
// drawExtendedArrow parameter indicates, for extendPath = true case, whether the path line is capped with an arrow head.
const drawSquarePath = function(
	context,
	geom,
	cS,
	firstCoords,
	secondCoords,
	startingCells,
	exitData,
	hasXOverPath,
	doNotStroke,
	largeCircles,
	contextSettings,
	props = {}
) {
	const {
		replaceCirclesWithOutline,
		radiusRatio,
		exitLineCap,
		extendPath,
		drawExtendedArrow,
	} = props;
	if (!doNotStroke) {
		context.save();
		context.beginPath();
	}
	if (!_.isEmpty(contextSettings)) {
		_.each(contextSettings, (value, key) => {
			context[key] = value;
		});
	}

	let radiusSideRatio = largeCircles ? 0.45 : 0.38;
	if (replaceCirclesWithOutline) radiusSideRatio = 0;
	if (radiusRatio) radiusSideRatio = radiusRatio;
	// If path connects to a starting square, don't draw the part of the path
	// that's inside a circle around the center of starting square.
	let containsStartingSquare = false;
	const firstCoordString = firstCoords.join(",");
	const secondCoordString = secondCoords.join(",");
	const startingSquareStrings = _.map(startingCells, cell => cell.join(","));
	const firstIsStart = _.includes(startingSquareStrings, firstCoordString);
	const secondIsStart = _.includes(startingSquareStrings, secondCoordString);
	if (firstIsStart || secondIsStart) {
		const startCoords = firstIsStart ? firstCoords : secondCoords;
		const otherCoords = firstIsStart ? secondCoords : firstCoords;
		const startDrawCoords = geom.getDrawCellCenter(
			startCoords[0],
			startCoords[1]
		);
		const otherDrawCoords = geom.getDrawCellCenter(
			otherCoords[0],
			otherCoords[1]
		);
		const startCircleRadius = geom.dim.size * radiusSideRatio * 1.066;
		const dx = otherCoords[0] - startCoords[0];
		const dy = otherCoords[1] - startCoords[1];
		context.moveTo(
			startDrawCoords[0] + dx * startCircleRadius,
			startDrawCoords[1] + dy * startCircleRadius
		);
		if (firstIsStart && secondIsStart) {
			context.lineTo(
				otherDrawCoords[0] - dx * startCircleRadius,
				otherDrawCoords[1] - dy * startCircleRadius
			);
		} else {
			context.lineTo(otherDrawCoords[0], otherDrawCoords[1]);
		}
		containsStartingSquare = true;
	}
	// Otherwise, draw a path connecting the centers of the two squares.
	if (!containsStartingSquare) {
		const firstDrawCoords = geom.getDrawCellCenter(
			firstCoords[0],
			firstCoords[1]
		);
		const secondDrawCoords = geom.getDrawCellCenter(
			secondCoords[0],
			secondCoords[1]
		);
		context.moveTo(firstDrawCoords[0], firstDrawCoords[1]);
		context.lineTo(secondDrawCoords[0], secondDrawCoords[1]);
	}
	// Extend the path if the maze exit is one of the coordinates.
	if (
		exitData &&
		(_.isEqual(firstCoords, exitData.pathEnd) ||
			_.isEqual(secondCoords, exitData.pathEnd))
	) {
		// Draw the extension from the center of the exit square...
		const pathEnd = exitData.pathEnd;
		const exitSquareCoords = geom.getDrawCellCenter(pathEnd[0], pathEnd[1]);
		const exitSquareRect = geom.getDrawCellRect(pathEnd[0], pathEnd[1]);
		let exitCoords = exitSquareCoords;

		context.moveTo(exitSquareCoords[0], exitSquareCoords[1]);
		if (exitLineCap !== "center") {
			// ... to the midpoint of the exit edge.
			const edge = exitData.pathEndEdge;
			const exitEdgeCoords = geom.getDrawEdge(edge[0], edge[1], edge[2]);
			exitCoords = [
				(exitEdgeCoords[0] + exitEdgeCoords[2]) / 2,
				(exitEdgeCoords[1] + exitEdgeCoords[3]) / 2,
			];

			// end of path is extended out of the grid if desired
			const otherCell = geom.getOtherCell(edge, pathEnd);
			const dx = otherCell[0] - pathEnd[0];
			const dy = otherCell[1] - pathEnd[1];
			if (extendPath) {
				exitCoords[0] += (Math.sign(dx) * exitSquareRect.w * 2) / 3;
				exitCoords[1] += (Math.sign(dy) * exitSquareRect.h * 2) / 3;
			}

			context.lineTo(exitCoords[0], exitCoords[1]);

			// for extended path, the line can be capped with an arrow
			if (drawExtendedArrow && extendPath) {
				drawExtendedExitArrow(
					context,
					...exitCoords,
					exitCoords[0] + (Math.sign(dx) * exitSquareRect.w) / 2,
					exitCoords[1] + (Math.sign(dy) * exitSquareRect.h) / 2,
					{},
					true
				);
			}
		} else {
			const prevCoord = _.isEqual(firstCoords, exitData.pathEnd)
				? secondCoords
				: firstCoords;
			const dx = pathEnd[0] - prevCoord[0];
			const dy = pathEnd[1] - prevCoord[1];
			if (drawExtendedArrow) {
				const aSpan = [exitSquareRect.w / 4, exitSquareRect.h / 4];
				drawExtendedExitArrow(
					context,
					...exitCoords,
					exitCoords[0] + Math.sign(dx) * aSpan[0],
					exitCoords[1] + Math.sign(dy) * aSpan[1],
					{
						arrowLength: aSpan[0] * 1.75,
						arrowHeight: aSpan[1] * 1.5,
					},
					true
				);
			}
		}
	}
	if (!doNotStroke) {
		context.stroke();
		context.restore();
	}
};

const drawHexPath = function(
	context,
	geom,
	cS,
	firstCoords,
	secondCoords,
	startingHexes,
	exitData,
	hasXOverPath,
	doNotStroke,
	largeCircles,
	contextSettings,
	props = {}
) {
	const {replaceCirclesWithOutline, radiusRatio} = props;
	if (!doNotStroke) {
		context.save();
		context.beginPath();
		context.lineWidth = context.lineWidth * HEX_PATH_LINE_WIDTH;
	}
	if (!_.isEmpty(contextSettings)) {
		_.each(contextSettings, (value, key) => {
			context[key] = value;
		});
	}

	let radiusSideRatio = largeCircles ? 0.45 : 0.38;
	if (replaceCirclesWithOutline) radiusSideRatio = 0;
	if (radiusRatio) radiusSideRatio = radiusRatio;
	// If path connects to a starting square, don't draw the part of the path
	// that's inside a circle around the center of starting square.
	let containsStartingHex = false;
	const firstCoordString = firstCoords.join(",");
	const secondCoordString = secondCoords.join(",");
	const startingHexStrings = _.map(startingHexes, hex => hex.join(","));
	const firstIsStart = _.includes(startingHexStrings, firstCoordString);
	const secondIsStart = _.includes(startingHexStrings, secondCoordString);
	if (firstIsStart || secondIsStart) {
		const startCoords = firstIsStart ? firstCoords : secondCoords;
		const otherCoords = firstIsStart ? secondCoords : firstCoords;
		const startDrawCoords = geom.getDrawCellCenter(
			startCoords[0],
			startCoords[1]
		);
		const otherDrawCoords = geom.getDrawCellCenter(
			otherCoords[0],
			otherCoords[1]
		);
		const startCircleRadius = geom.dim.size * radiusSideRatio * 1.066;
		const dx = otherCoords[0] - startCoords[0];
		const dy = otherCoords[1] - startCoords[1];
		context.moveTo(
			startDrawCoords[0] + dx * startCircleRadius,
			startDrawCoords[1] + dy * startCircleRadius
		);
		if (firstIsStart && secondIsStart) {
			context.lineTo(
				otherDrawCoords[0] - dx * startCircleRadius,
				otherDrawCoords[1] - dy * startCircleRadius
			);
		} else {
			context.lineTo(otherDrawCoords[0], otherDrawCoords[1]);
		}
		containsStartingHex = true;
	}
	// Otherwise, draw a path connecting the centers of the two hexes.
	if (!containsStartingHex) {
		const firstDrawCoords = geom.getDrawCellCenter(
			firstCoords[0],
			firstCoords[1]
		);
		const secondDrawCoords = geom.getDrawCellCenter(
			secondCoords[0],
			secondCoords[1]
		);
		///added a small offset which allowed drawing in Safari
		context.moveTo(firstDrawCoords[0], firstDrawCoords[1]);
		context.lineTo(secondDrawCoords[0], secondDrawCoords[1] + 0.01);
	}
	if (!doNotStroke) {
		context.stroke();
		context.restore();
	}
};

const drawExtendedExitArrow = function(
	context,
	x1,
	y1,
	x2,
	y2,
	arrowHeadParam,
	doNotStroke
) {
	let {arrowLength, arrowHeight} = arrowHeadParam;

	const dx = x2 - x1;
	const dy = y2 - y1;
	const hyp = Math.sqrt(dx * dx + dy * dy);

	if (!arrowLength) {
		arrowLength = 0.25 * hyp;
	}
	if (!arrowHeight) {
		arrowHeight = 0.5 * hyp;
	}
	ArrowDraw.drawStrokeArrow(context, {
		x1,
		y1,
		x2,
		y2,
		headHeight: arrowHeight,
		headLength: arrowLength,
		doNotStroke,
		continuePath: true,
	});
};

const drawCircleInCell = function(
	context,
	geom,
	cS,
	cellKey,
	largeCircles,
	forceColor,
	props = {}
) {
	const {radiusRatio, alphaMultiplier} = props;
	const startCoords = geom.convertCellKey(cellKey);
	const rect = geom.getDrawCellRect(startCoords[0], startCoords[1]);
	let radiusSideRatio = largeCircles ? 0.45 : 0.38;
	if (radiusRatio) radiusSideRatio = radiusRatio;
	context.save();
	if (forceColor) {
		context.fillStyle = forceColor;
	}
	context.globalAlpha = (alphaMultiplier || 1) * 0.6;
	context.beginPath();
	context.arc(
		rect.x + rect.w / 2,
		rect.y + rect.h / 2,
		geom.dim.size * radiusSideRatio,
		0,
		2 * Math.PI
	);
	context.closePath();
	context.fill();
	context.restore();
};

const drawMazeExit = function(context, geom, cS, exitData, isSmall) {
	const numTicks = isSmall ? 8 : 14;
	const tickThickness = isSmall ? 1 / 2 : 3 / 2;
	const tickLength = 1 / 7; // Will be scaled by squareSize.
	const tickOffset = 1 / numTicks; // Will be scaled by squareSize and tick index.

	const edge = exitData.pathEndEdge;
	const exitEdgeCoords = geom.getDrawEdge(edge[0], edge[1], edge[2]);
	const baseCoords = exitEdgeCoords.slice(0, 2);
	const dx = exitEdgeCoords[2] - exitEdgeCoords[0];
	const dy = exitEdgeCoords[3] - exitEdgeCoords[1];

	context.save();
	context.strokeStyle = cS.defaultWhiteFill;
	context.lineCap = "butt";
	context.lineWidth = context.lineWidth * 3;
	// Draw a white path over the maze exit edge.
	context.beginPath();
	context.moveTo(baseCoords[0], baseCoords[1]);
	context.lineTo(baseCoords[0] + dx, baseCoords[1] + dy);
	context.stroke();
	context.restore();
	// Draw the exit tick marks.
	context.save();
	context.lineWidth = context.lineWidth * tickThickness;
	for (let i = 0; i <= numTicks; i++) {
		const tickCoords = [
			baseCoords[0] + dx * i * tickOffset - (dy * tickLength) / 2,
			baseCoords[1] + dy * i * tickOffset - (dx * tickLength) / 2,
		];
		context.beginPath();
		context.moveTo(tickCoords[0], tickCoords[1]);
		context.lineTo(
			tickCoords[0] + dy * tickLength,
			tickCoords[1] + dx * tickLength
		);
		context.stroke();
	}
	context.restore();
};

const drawMazeExitLabel = function(
	exitLabel,
	context,
	geom,
	cS,
	exitData,
	opts = {}
) {
	const {lessNumberPadding = false, sizeFontForNumBatexChars = false} = opts;
	let {fontSize} = opts;

	if (!exitLabel) return;

	const pathEnd = exitData.pathEnd;
	const pathEndRect = geom.getDrawCellRect(...pathEnd);

	const edge = exitData.pathEndEdge;
	const otherCell = geom.getOtherCell(edge, pathEnd);

	const dx = otherCell[0] - pathEnd[0];
	const dy = otherCell[1] - pathEnd[1];

	context.save();
	const yPadding = lessNumberPadding
		? Math.max(3, geom.dim.size / 6)
		: Math.max(5, geom.dim.size / 5);
	const xPadding = lessNumberPadding ? 1 : 0;
	const drawOptions = {
		batex: exitLabel,
		rect: [
			pathEndRect.x + Math.sign(dx) * pathEndRect.w + xPadding,
			pathEndRect.y + Math.sign(dy) * pathEndRect.h + yPadding,
			pathEndRect.w - 2 * xPadding,
			pathEndRect.h - 2 * yPadding,
		],
	};

	//If sizeFontForNumBatexChars is given, use that to compute the fontSize that should be used
	if (sizeFontForNumBatexChars) {
		fontSize = (geom.dim.size * 4) / 3 / (sizeFontForNumBatexChars + 0.2);
	}

	if (fontSize) {
		drawOptions.scaleOff = true;
		drawOptions.fontInfo = {
			size: fontSize,
			family: ["Roboto"],
		};
	}
	context.fillStyle = cS.defaultText;
	CanvasMath.drawInRect(context, drawOptions);
	context.restore();
};

const drawMouseOverSquare = function(context, geom, cS, square, color, dashed) {
	context.save();
	const mousedRect = geom.getDrawCellRect(square[0], square[1]);
	const c = color === "eraser" ? Color(cS.incorrectFill) : Color(color);
	if (dashed)
		context.setLineDash([context.lineWidth * 6, context.lineWidth * 4.5]);
	context.strokeStyle = c.darken(0.1).string();
	context.lineWidth *= dashed ? 2 : 3;

	context.lineJoin = "round";
	context.strokeRect(mousedRect.x, mousedRect.y, mousedRect.w, mousedRect.h);
	context.restore();
};

const drawSquareOutline = function(
	context,
	geom,
	cS,
	square,
	color,
	options = {}
) {
	context.save();
	const mousedRect = geom.getDrawCellRect(square[0], square[1]);
	context.strokeStyle = color;
	context.lineWidth *= options.hasThickerOutline ? 3 : 2;
	if (options.lineJoin) {
		context.lineJoin = options.lineJoin;
	}
	// context.lineJoin = "round";
	if (options.dashed) {
		context.setLineDash([context.lineWidth * 2.5, context.lineWidth * 2.5]);
		context.lineCap = "round";
	}
	context.strokeRect(mousedRect.x, mousedRect.y, mousedRect.w, mousedRect.h);
	context.restore();
};

const drawCollinearGroupOutline = function(
	context,
	geom,
	cS,
	cellPairs,
	props = {}
) {
	const {color, hasThickerOutline} = props;
	context.save();
	context.strokeStyle = color || cS.textBlack;
	if (cellPairs) {
		cellPairs.forEach(crds => {
			const from = crds.slice(0, 2);
			const to = crds.slice(2, crds.length);
			if (from && to) {
				const startCell = geom.getDrawCellRect(from[0], from[1]);
				const endCell = geom.getDrawCellRect(to[0], to[1]);
				const s = startCell;
				const e = endCell;
				const rect = [
					Math.min(s.x, e.x),
					Math.min(s.y, e.y),
					Math.abs(s.x - e.x) + (s.x < e.x ? e.w : s.w),
					Math.abs(s.y - e.y) + (s.y < e.y ? e.h : s.h),
				];
				DrawRectUtil.drawStrokeRect(context, rect, {
					hasThickerOutline,
				});
			}
		});
		context.restore();
	}
};

const drawMouseOverEdge = function(context, geom, cS, edge, isX, opts = {}) {
	const {widthMultiplier, forceColor, xColor} = opts;

	if (edge) {
		context.save();
		const edgeCoords = geom.getDrawEdge(edge[0], edge[1], edge[2]);
		if (!isX) {
			context.beginPath();
			context.strokeStyle = Color(forceColor || cS.inputText)
				.fade(0.7)
				.string();
			context.lineWidth = context.lineWidth * 2 * (widthMultiplier || 1);
			context.lineCap = "round";
			context.moveTo(edgeCoords[0], edgeCoords[1]);
			context.lineTo(edgeCoords[2], edgeCoords[3]);
			context.stroke();
		} else {
			drawX(
				context,
				geom,
				cS,
				(edgeCoords[0] + edgeCoords[2]) / 2,
				(edgeCoords[1] + edgeCoords[3]) / 2,
				xColor
			);
		}
		context.restore();
	}
};

const drawPotentialSquarePath = function(
	context,
	geom,
	cS,
	squarePath,
	props = {}
) {
	const {
		exitData,
		startingSquares,
		strokeStyle,
		alphaMultiplier,
		largeCircles,
		replaceCirclesWithOutline,
		radiusRatio,
		exitLineCap,
		extendPath,
		drawExtendedArrow,
		onlyDrawFullPathExitArrow,
		allPathsLength,
		numberOfRemovedSquares,
	} = props;
	if (squarePath) {
		const contextSettings = {
			lineWidth: context.lineWidth * 4,
			globalAlpha: (alphaMultiplier || 1) * 0.5,
			lineCap: "round",
			strokeStyle: strokeStyle || cS.lightGrayFill,
		};
		drawSquarePath(
			context,
			geom,
			cS,
			squarePath[0],
			squarePath[1],
			startingSquares || [],
			exitData || false,
			false,
			false,
			largeCircles,
			contextSettings,
			{
				replaceCirclesWithOutline,
				radiusRatio,
				exitLineCap,
				extendPath,
				drawExtendedArrow:
					drawExtendedArrow && !onlyDrawFullPathExitArrow
						? true
						: allPathsLength ===
						  geom.getAllCells().length - (numberOfRemovedSquares || 0),
			}
		);
	}
};

const drawPotentialHexPath = function(context, geom, cS, hexPath, props = {}) {
	const {
		exitData,
		startingHexes,
		strokeStyle,
		alphaMultiplier,
		largeCircles,
		replaceCirclesWithOutline,
		radiusRatio,
		exitLineCap,
		extendPath,
		drawExtendedArrow,
		onlyDrawFullPathExitArrow,
		allPathsLength,
		numberOfRemovedHexes,
	} = props;
	if (hexPath) {
		const contextSettings = {
			lineWidth: context.lineWidth * hexSizeMultiplier,
			globalAlpha: (alphaMultiplier || 1) * 0.5,
			lineCap: "round",
			strokeStyle: strokeStyle || cS.lightGrayFill,
		};
		drawHexPath(
			context,
			geom,
			cS,
			hexPath[0],
			hexPath[1],
			startingHexes || [],
			exitData || false,
			false,
			false,
			largeCircles,
			contextSettings,
			{
				replaceCirclesWithOutline,
				radiusRatio,
				exitLineCap,
				extendPath,
				drawExtendedArrow:
					drawExtendedArrow && !onlyDrawFullPathExitArrow
						? true
						: allPathsLength ===
						  geom.getAllCells().length - (numberOfRemovedHexes || 0),
			}
		);
	}
};

const drawMouseOverHexPath = function(context, geom, cS, hexes) {
	//hexes contains the path
	if (hexes) {
		context.save();

		//get the edge between the two cells
		const edgeCoords = geom.getIntersectingEdgeBetweenTwoCells(
			hexes[0],
			hexes[1]
		);
		//get the draw edge of the edge
		const drawCoords = geom.getDrawEdge(
			edgeCoords[0],
			edgeCoords[1],
			edgeCoords[2]
		);
		//draw the x
		drawX(
			context,
			geom,
			cS,
			(drawCoords[0] + drawCoords[2]) / 2,
			(drawCoords[1] + drawCoords[3]) / 2,
			undefined,
			true
		);
		context.restore();
	}
};
// The largeCircles parameter indicates if the circles are larger than average.
const drawXOnPath = function(
	context,
	geom,
	cS,
	cells,
	startingCells,
	largeCircles,
	props = {}
) {
	const {radiusRatio, replaceCirclesWithOutline} = props;
	let radiusSideRatio = largeCircles ? 0.45 : 0.38;
	if (replaceCirclesWithOutline) radiusSideRatio = 0;
	if (radiusRatio) radiusSideRatio = radiusRatio;

	if (cells) {
		context.save();
		// If path connects to the circle over one of the starting cells, we need to shift the X.
		const startingCellKeys = _.map(startingCells, cell => cell.join(","));
		const firstIsStart = _.includes(startingCellKeys, cells[0].join(","));
		const secondIsStart = _.includes(startingCellKeys, cells[1].join(","));
		if (firstIsStart || secondIsStart) {
			const startingCell = firstIsStart ? cells[0] : cells[1];
			const otherCell = firstIsStart ? cells[1] : cells[0];
			const startCoords = geom.getDrawCellCenter(...startingCell);
			const otherCoords = geom.getDrawCellCenter(...otherCell);
			const startCircleRadius = geom.dim.size * radiusSideRatio * 1.066;
			const dx = otherCell[0] - startingCell[0];
			const dy = otherCell[1] - startingCell[1];
			const xOffset =
				firstIsStart && secondIsStart ? 0 : dx * startCircleRadius;
			const yOffset =
				firstIsStart && secondIsStart ? 0 : dy * startCircleRadius;
			drawX(
				context,
				geom,
				cS,
				(startCoords[0] + xOffset + otherCoords[0]) / 2,
				(startCoords[1] + yOffset + otherCoords[1]) / 2
			);
		} else {
			// Otherwise, we put the X directly between the endpoints of the path.
			const edgeCoords = geom.getDrawEdge(cells[1][0], cells[1][1], !cells[2]);
			drawX(
				context,
				geom,
				cS,
				(edgeCoords[0] + edgeCoords[2]) / 2,
				(edgeCoords[1] + edgeCoords[3]) / 2
			);
		}
		context.restore();
	}
};

const drawX = function(context, geom, cS, x, y, color, isLarge, isDashed) {
	context.save();
	context.beginPath();
	context.strokeStyle = color || cS.deletionX;
	context.lineCap = "round";
	context.lineWidth = context.lineWidth * 2;
	const offset = isLarge ? geom.dim.size / 5 : geom.dim.size / 6;
	if (isDashed) {
		context.globalAlpha *= 0.45;
		const dashLength =
			Math2D.pointPointDistance(
				x - offset,
				y - offset,
				x + offset,
				y + offset
			) / 7;
		context.setLineDash([dashLength]);
	}
	context.moveTo(x - offset, y - offset);
	context.lineTo(x + offset, y + offset);
	context.moveTo(x + offset, y - offset);
	context.lineTo(x - offset, y + offset);
	context.stroke();
	context.restore();
};

const drawO = function(context, geom, cS, x, y, color) {
	context.save();
	context.strokeStyle = color || cS.defaultBlackFill;
	context.lineWidth = context.lineWidth * 1.5;
	const offset = geom.dim.size / 2.4;
	context
		.bp()
		.arc(x, y, offset, 0, 2 * Math.PI)
		.stroke();
	context.restore();
};

//pattern is a number associated with the index of the color selected/position of crayon in crayon box.
const drawPattern = function(context, rect, cS, pattern, isMouseOver, isXOver) {
	let position;
	const padding = rect.w / 8;
	const r = rect.w / 16;
	switch (pattern) {
		case 0:
			position = {
				x: rect.x + padding,
				y: rect.y + padding,
			};
			break;
		case 1:
			position = {
				x: rect.x + rect.w - padding,
				y: rect.y + rect.h - padding,
			};
			break;
	}
	if (position) {
		context.save();
		context.fillStyle = cS.defaultBlackFill;
		if (isMouseOver) {
			context.fillStyle = cS.lightGrayFill;
		}
		context.beginPath();
		context.arc(position.x, position.y, r, 0, Math.PI * 2);
		context.closePath();
		context.fill();
		context.restore();
	}
};

const drawFilledRect = function(
	context,
	geom,
	cS,
	rect,
	drawFilled,
	thinnerOutline,
	displayArea
) {
	context.save();
	const unitSquare = geom.getDrawCellRect(rect.x, rect.y);
	context.fillStyle = rect.color;
	const rectX = unitSquare.x;
	const rectY = unitSquare.y;
	const rectWidth = unitSquare.w * rect.width;
	const rectHeight = unitSquare.h * rect.height;

	const x = rectX;
	const y = rectY;

	if (drawFilled) {
		const offset = unitSquare.w / 10;
		const alpha = _.has(rect, "alpha") ? rect.alpha : 0.6;
		context.strokeStyle = rect.color;
		context.fillStyle = rect.color;
		context.globalAlpha *= alpha;
		context.lineWidth = offset;
		context.fillRect(x, y, rectWidth, rectHeight);
		if (!rect.noStroke) {
			context.strokeRect(
				x + offset / 2,
				y + offset / 2,
				rectWidth - offset,
				rectHeight - offset
			);
		}
	} else {
		context.lineWidth = context.lineWidth * (thinnerOutline ? 3 : 4);
		context.lineCap = "round";
		context.strokeRect(x, y, rectWidth, rectHeight);
	}
	context.restore();
	if (displayArea) {
		context.save();
		const fontSize = geom.dim.size;
		context.fillStyle = cS.white;
		CanvasMath.drawInRect(context, {
			batex: "" + rect.width * rect.height,
			rect: [x, y, rectWidth, rectHeight],
			scaleDownOnly: true,
			fontInfo: {size: fontSize},
		});
		context.restore();
	}
};

const drawInnerRect = function(context, geom, cS, rect, drawFilled) {
	context.save();
	const unitSquare = geom.getDrawCellRect(rect.x, rect.y);
	context.fillStyle = rect.color;
	const rectX = unitSquare.x;
	const rectY = unitSquare.y;
	const rectWidth = unitSquare.w * rect.width;
	const rectHeight = unitSquare.h * rect.height;
	if (drawFilled) {
		const offset = unitSquare.w / 8;
		context.strokeStyle = rect.color;
		context.fillStyle = rect.color;
		context.globalAlpha = 0.6;
		context.lineWidth = offset / 4;
		context.fillRect(
			rectX + offset,
			rectY + offset,
			rectWidth - 2 * offset,
			rectHeight - 2 * offset
		);
		context.globalAlpha = 0.8;
		context.strokeRect(
			rectX + offset,
			rectY + offset,
			rectWidth - 2 * offset,
			rectHeight - 2 * offset
		);
	} else {
		context.lineWidth = context.lineWidth * 4;
		context.lineCap = "round";
		context.strokeRect(rectX, rectY, rectWidth, rectHeight);
	}
	context.restore();
};

module.exports = {
	drawGrid,
	drawPartialGrid,
	drawGroupGrid,
	drawGroupHover,
	drawGroupForceSelectedSquares,
	drawGroupSelectedSquares,
	drawGroupReaction,
	drawPartsCorrectXMarks,
	drawNumbers,
	drawNumberInCell,
	drawLabelInCell,
	drawImageInCell,
	drawGridBorder,
	drawSelectedSquares,
	drawSelectedDiagonals,
	drawSelectedEdges,
	drawEdges,
	drawHexEdges,
	drawVertexDot,
	drawAllSquarePaths,
	drawAllHexPaths,
	drawPotentialSquarePath,
	drawPotentialHexPath,
	drawCircleInCell,
	drawMazeExit,
	drawMazeExitLabel,
	drawMouseOverSquare,
	drawSquareOutline,
	drawCollinearGroupOutline,
	drawMouseOverEdge,
	drawExtendedEdge,
	drawXOnPath,
	drawSquarePath,
	drawHexPath,
	drawX,
	drawO,
	drawFilledRect,
	drawInnerRect,
	drawSquareOutlineColor,
	drawSquareReaction,
	drawMouseOverHexPath,
	DIAGONAL_DIRECTIONS,
};
